import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { GstProvider } from "@/context/GstContext";
import { LangProvider } from "@/context/LangContext";
import { Toaster } from "sonner";
import InstallPrompt from "@/components/InstallPrompt";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppLayout from "@/pages/AppLayout";
import Dashboard from "@/pages/Dashboard";
import GstCalc from "@/pages/calculators/GstCalc";
import QuoteCalc from "@/pages/calculators/QuoteCalc";
import MileageCalc from "@/pages/calculators/MileageCalc";
import TaxCalc from "@/pages/calculators/TaxCalc";
import MarginMarkupCalc from "@/pages/calculators/MarginMarkupCalc";
import BusinessPersonalCalc from "@/pages/calculators/BusinessPersonalCalc";
import BudgetCalc from "@/pages/calculators/BudgetCalc";
import RideshareCalc from "@/pages/calculators/RideshareCalc";
import UberWorthItLanding from "@/pages/UberWorthItLanding";
import Clients from "@/pages/Clients";
import Jobs from "@/pages/Jobs";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground font-mono text-sm">
        LOADING…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app/dashboard" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <LangProvider>
      <GstProvider>
      <AuthProvider>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#121212",
            border: "1px solid #27272a",
            color: "#fff",
            fontFamily: "JetBrains Mono, monospace",
            borderRadius: 0,
          },
        }}
      />
      <InstallPrompt />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Standalone shareable landing for social media (no app chrome) */}
          <Route path="/uber-worth-it" element={<UberWorthItLanding />} />
          <Route path="/is-uber-driving-worth-it" element={<UberWorthItLanding />} />

          {/* Guest-accessible calculators */}
          <Route path="/tools" element={<AppLayout guestMode />}>
            <Route index element={<Navigate to="gst" replace />} />
            <Route path="gst" element={<GstCalc />} />
            <Route path="quote" element={<QuoteCalc />} />
            <Route path="mileage" element={<MileageCalc />} />
            <Route path="tax" element={<TaxCalc />} />
            <Route path="margin" element={<MarginMarkupCalc />} />
            <Route path="expense" element={<BusinessPersonalCalc />} />
            <Route path="budget" element={<BudgetCalc />} />
            <Route path="rideshare" element={<RideshareCalc />} />
          </Route>

          {/* Authenticated app */}
          <Route
            path="/app"
            element={
              <Protected>
                <AppLayout />
              </Protected>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="calc/gst" element={<GstCalc />} />
            <Route path="calc/quote" element={<QuoteCalc />} />
            <Route path="calc/mileage" element={<MileageCalc />} />
            <Route path="calc/tax" element={<TaxCalc />} />
            <Route path="calc/margin" element={<MarginMarkupCalc />} />
            <Route path="calc/expense" element={<BusinessPersonalCalc />} />
            <Route path="calc/budget" element={<BudgetCalc />} />
            <Route path="calc/rideshare" element={<RideshareCalc />} />
            <Route path="clients" element={<Clients />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="settings" element={<Settings />} />
            <Route
              path="admin"
              element={
                <Protected admin>
                  <Admin />
                </Protected>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </GstProvider>
    </LangProvider>
    </ThemeProvider>
  );
}

export default App;
